import type { ProjectTask, TaskDependencyEdge, DependencyEdgeType } from '../types';

/** Clamp stored lag; values are always business days (weekdays), same as `lagBusinessDays` on edges. */
function normalizeLag(raw: unknown): number {
  const n = Math.floor(Number(raw) || 0);
  return Math.max(-500, Math.min(500, n));
}

function normalizeDepType(raw: unknown): DependencyEdgeType {
  return raw === 'SS' || raw === 'FS' || raw === 'FF' || raw === 'SF' ? raw : 'FS';
}

export function getDependencyEdges(task: ProjectTask): TaskDependencyEdge[] {
  if (task.dependencyEdges && task.dependencyEdges.length > 0) {
    return task.dependencyEdges.map((e) => ({
      predecessorId: e.predecessorId,
      type: normalizeDepType(e.type),
      lagBusinessDays: normalizeLag(e.lagBusinessDays),
    }));
  }
  return (task.dependencies || []).map((predecessorId) => ({
    predecessorId,
    type: 'FS' as const,
    lagBusinessDays: 0,
  }));
}

/** Persist edges and mirror legacy `dependencies` for older readers. */
export function dependencyPayload(edges: TaskDependencyEdge[]): Pick<ProjectTask, 'dependencyEdges' | 'dependencies'> {
  const normalized = edges.map((e) => ({
    predecessorId: e.predecessorId,
    type: normalizeDepType(e.type),
    lagBusinessDays: normalizeLag(e.lagBusinessDays),
  }));
  return {
    dependencyEdges: normalized,
    dependencies: normalized.map((e) => e.predecessorId),
  };
}

export function findEdge(edges: TaskDependencyEdge[], predecessorId: string): TaskDependencyEdge | undefined {
  return edges.find((e) => e.predecessorId === predecessorId);
}

/** Tasks that list `predId` as a predecessor (successors in the pred→task DAG). */
export function successorTaskIds(tasks: ProjectTask[], projectId: string, predId: string): string[] {
  return tasks
    .filter((t) => t.projectId === projectId && t.id && getDependencyEdges(t).some((e) => e.predecessorId === predId))
    .map((t) => t.id!);
}

/** True if this task has at least one FS/SS edge to another task in the same project (incoming or outgoing). */
export function taskParticipatesInDependencyGraph(
  tasks: ProjectTask[],
  projectId: string,
  taskId: string,
): boolean {
  const inProj = tasks.filter((t) => t.projectId === projectId && t.id);
  const idSet = new Set(inProj.map((t) => t.id!));
  if (!idSet.has(taskId)) return false;
  const self = inProj.find((t) => t.id === taskId);
  if (self && getDependencyEdges(self).some((e) => idSet.has(e.predecessorId))) return true;
  for (const o of inProj) {
    if (o.id === taskId) continue;
    if (getDependencyEdges(o).some((e) => e.predecessorId === taskId)) return true;
  }
  return false;
}

/**
 * Undirected connected component: dependency edges link successor task ↔ predecessor task.
 * Used to shift all linked bars by the same calendar delta (“move whole chain”).
 */
export function taskIdsInUndirectedDependencyComponent(
  tasks: ProjectTask[],
  projectId: string,
  rootId: string,
): Set<string> {
  const inProj = tasks.filter((t) => t.projectId === projectId && t.id);
  const idSet = new Set(inProj.map((t) => t.id!));
  if (!idSet.has(rootId)) return new Set();

  const adj = new Map<string, Set<string>>();
  for (const id of Array.from(idSet)) adj.set(id, new Set());

  for (const t of inProj) {
    const tid = t.id!;
    for (const e of getDependencyEdges(t)) {
      if (!idSet.has(e.predecessorId)) continue;
      adj.get(tid)!.add(e.predecessorId);
      adj.get(e.predecessorId)!.add(tid);
    }
  }

  const out = new Set<string>();
  const stack = [rootId];
  out.add(rootId);
  while (stack.length) {
    const cur = stack.pop()!;
    for (const nb of Array.from(adj.get(cur) || [])) {
      if (!out.has(nb)) {
        out.add(nb);
        stack.push(nb);
      }
    }
  }
  return out;
}

/** Returns true if making `taskId` depend on `predecessorId` would create a cycle. */
export function wouldAddingEdgeCreateCycle(
  tasks: ProjectTask[],
  taskId: string,
  predecessorId: string,
  projectId: string,
): boolean {
  if (predecessorId === taskId) return true;
  const ids = new Set(tasks.filter((t) => t.projectId === projectId && t.id).map((t) => t.id!));
  if (!ids.has(predecessorId) || !ids.has(taskId)) return false;

  const stack = [taskId];
  const visited = new Set<string>();
  while (stack.length) {
    const cur = stack.pop()!;
    if (cur === predecessorId) return true;
    if (visited.has(cur)) continue;
    visited.add(cur);
    for (const sid of successorTaskIds(tasks, projectId, cur)) {
      if (sid === taskId) continue;
      stack.push(sid);
    }
  }
  return false;
}
